import { z } from "zod";
import { InterviewResponseSchema } from "../message/respond-to-interview";

const DistributionPayloadSchema = z.object({
  jobId: z.string(),
  organizationId: z.string(),
  title: z.string(),
  description: z.string(),
  companyName: z.string(),
  location: z.string(),
  commitment: z.string(),
  compensation: z.string(),
  listingUrl: z.string(),
});

export const EventSchemas = {
  // ── Email events ───────────────────────────────────────────────
  "email/organization.invites": z.object({
    organizationId: z.string(),
    invites: z.array(
      z.object({
        to: z.string(),
        from: z.string(),
        invitedByEmail: z.string(),
        invitedByName: z.string(),
        recipientEmail: z.string(),
        organizationName: z.string(),
        inviteUrl: z.url(),
      })
    ),
  }),

  "email/auth.magic-link": z.object({
    recipientEmail: z.string(),
    to: z.string(),
    from: z.string(),
    actionUrl: z.url(),
    isExistingUser: z.boolean(),
  }),

  "email/application.submitted": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    jobTitle: z.string(),
    workplaceName: z.string(),
    applicationUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.new": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    candidateName: z.string(),
    jobTitle: z.string(),
    applicationReviewUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.draft_reminder": z.object({
    userId: z.string(),
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string().nullish(),
    jobTitle: z.string(),
    workplaceName: z.string(),
    applicationUrl: z.url(),
    stage: z.number(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.daily_digest": z.object({
    userId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    totalUnreviewed: z.number(),
    totalStrongMatches: z.number(),
    jobs: z.array(
      z.object({
        jobTitle: z.string(),
        unreviewedCount: z.number(),
        strongMatches: z.number(),
        manageJobUrl: z.url(),
      })
    ),
    jobsUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/job.nearby_recommendations": z.object({
    userId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string().nullish(),
    jobs: z.array(
      z.object({
        jobTitle: z.string(),
        workplaceName: z.string(),
        distanceKm: z.number(),
        listingUrl: z.url(),
      })
    ),
    listingsUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.shortlisted": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    workplaceName: z.string(),
    jobTitle: z.string(),
    applicationUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.interviewing": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    workplaceName: z.string(),
    jobTitle: z.string(),
    applicationUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.accepted": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    workplaceName: z.string(),
    jobTitle: z.string(),
    applicationUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/application.rejected": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    workplaceName: z.string(),
    jobTitle: z.string(),
    jobListingsUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/job.archived": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    jobTitle: z.string(),
    workplaceName: z.string(),
  }),

  "email/job.published": z.object({
    organizationId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    publicJobListingUrl: z.url(),
    manageJobUrl: z.url(),
    jobTitle: z.string(),
    workplaceName: z.string(),
  }),

  "email/user.feedback": z.object({
    userId: z.string(),
    to: z.string(),
    from: z.string(),
    submitterName: z.string(),
    score: z.number(),
    feedback: z.string(),
    pagePath: z.string(),
  }),

  "email/user.onboarding_completion_reminder": z.object({
    userId: z.string(),
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    recipientRole: z.enum(["employee", "employer"]),
    stage: z.number().int().min(1),
    onboardingUrl: z.url(),
    unsubscribeUrl: z.url(),
  }),

  "email/user.support": z.object({
    userId: z.string(),
    to: z.string(),
    from: z.string(),
    replyTo: z.string(),
    submitterName: z.string(),
    subject: z.string(),
    feature: z.string(),
    description: z.string(),
    severity: z.string(),
  }),

  "email/interview.invite": z.object({
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    organizationName: z.string(),
    interviewTitle: z.string(),
    conversationUrl: z.string(),
    interviewDate: z.string(),
  }),

  "email/interview.response": z.object({
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    candidateName: z.string(),
    interviewTitle: z.string(),
    response: InterviewResponseSchema,
    conversationUrl: z.string(),
  }),

  "email/interview.reminder": z.object({
    to: z.string(),
    from: z.string(),
    recipientName: z.string(),
    recipientRole: z.enum(["employee", "employer"]),
    kind: z.enum(["response_nudge", "day_before", "hour_before"]),
    interviewTitle: z.string(),
    interviewDate: z.string(),
    candidateName: z.string(),
    organizationName: z.string(),
    jobTitle: z.string(),
    conversationUrl: z.string(),
  }),

  "email/conversation.unread-summary": z.object({
    userId: z.string(),
    sinceDate: z.string().optional(),
    currentLevel: z.number().default(0),
  }),

  // ── AI events ──────────────────────────────────────────────────
  "ai/screening.score": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  "ai/cv.extract": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  "ai/user-cv.extract": z.object({
    userCvId: z.string(),
  }),

  "ai/cv.extracted": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  "ai/match.completed": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  "ai/summary.refresh": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  "ai/ranking.evaluate": z.object({
    jobId: z.string(),
  }),

  "ai/vetting.top-candidates.completed": z.object({
    jobId: z.string(),
  }),

  "ai/interview-questions.generate": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  "application/interview-progression.dispatch": z.object({
    dispatchId: z.string().uuid(),
  }),

  "interview/reminder.dispatch": z.object({
    dispatchId: z.string().uuid(),
  }),

  // ── Google events ──────────────────────────────────────────────
  "google/indexing.notify": z.object({
    url: z.string().url(),
    type: z.enum(["URL_UPDATED", "URL_DELETED"]),
  }),

  // ── Vetting events ─────────────────────────────────────────────
  "vetting/mie.status_changed": z.object({
    vettingId: z.string(),
    status: z.enum(["pending", "completed", "failed"]),
  }),

  "vetting/id.verify": z.object({
    vettingId: z.string(),
    type: z.literal("id_verification"),
    applicationId: z.string().optional(),
    jobId: z.string().optional(),
    autoVet: z.boolean().optional(),
  }),

  "vetting/id.verify.finished": z.object({
    vettingId: z.string(),
    applicationId: z.string().optional(),
    jobId: z.string().optional(),
    verified: z.boolean(),
    autoVet: z.boolean().optional(),
  }),

  "vetting/application.ensure-required": z.object({
    applicationId: z.string(),
    jobId: z.string(),
    reason: z.enum(["shortlisted", "interviewing", "acceptance_requested"]),
  }),

  "vetting/application.clearance.evaluate": z.object({
    applicationId: z.string(),
    jobId: z.string(),
  }),

  // ── Distribution events ───────────────────────────────────────
  "distribution/job.publish": DistributionPayloadSchema,

  "distribution/linkedin.post": DistributionPayloadSchema,

  "distribution/facebook.post": DistributionPayloadSchema,

  "distribution/whatsapp.post": DistributionPayloadSchema,

  "distribution/telegram.post": DistributionPayloadSchema,

  "distribution/email-subscribers.send": DistributionPayloadSchema,
} as const;
